import React, { useEffect, useRef, useState } from "react";
import ReportPeriodSelector from "./ReportPeriodSelector";
import ReportGrid from "./ReportGrid";
import CenterLoader from "../CenterLoader";

const concurrentDashboardRequests = 6;

function createRequestQueue(limit) {
    let activeRequests = 0;
    let cancelled = false;
    const pendingRequests = [];

    const runNext = () => {
        if (
            cancelled ||
            activeRequests >= limit ||
            pendingRequests.length === 0
        ) {
            return;
        }

        const request = pendingRequests.shift();
        activeRequests += 1;
        Promise.resolve()
            .then(request.task)
            .then(request.resolve, request.reject)
            .finally(() => {
                activeRequests -= 1;
                runNext();
            });
        runNext();
    };

    return {
        add(task) {
            if (cancelled) {
                return Promise.reject(new Error("Dashboard request cancelled"));
            }

            return new Promise((resolve, reject) => {
                pendingRequests.push({ task, resolve, reject });
                runNext();
            });
        },
        cancel() {
            cancelled = true;
            pendingRequests.splice(0).forEach(({ reject }) => {
                reject(new Error("Dashboard request cancelled"));
            });
        },
    };
}

function ReportDashboardPage({
    title,
    subtitle,
    fetchDashboardManifest,
    fetchDashboardBatch,
    filters,
    onFiltersChange,
    extraFilters,
    contained = true,
}) {
    const [dashboard, setDashboard] = useState(null);
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [retrying, setRetrying] = useState({});
    const [retryErrors, setRetryErrors] = useState({});
    const requestIdRef = useRef(0);
    const requestQueueRef = useRef(null);
    const abortControllerRef = useRef(null);

    useEffect(() => {
        let mounted = true;
        const requestId = requestIdRef.current + 1;
        requestIdRef.current = requestId;
        const controller = new AbortController();
        abortControllerRef.current = controller;
        const requestQueue = createRequestQueue(concurrentDashboardRequests);
        requestQueueRef.current = requestQueue;
        setDashboard(null);
        setReports([]);
        setLoading(true);
        setError(false);
        setRetrying({});
        setRetryErrors({});

        const replaceBatch = (batchKey, loadedReports) => {
            const loadedById = loadedReports.reduce((items, report) => {
                items[report.id] = report;
                return items;
            }, {});
            setReports((current) => {
                return current.map((report) => {
                    const loaded = loadedById[report.id];
                    if (report.batchKey !== batchKey || !loaded) {
                        return report;
                    }

                    return {
                        ...loaded,
                        batchKey,
                        status: "ready",
                    };
                });
            });
        };

        const failBatch = (batchKey) => {
            setReports((current) => {
                return current.map((report) => {
                    if (report.batchKey !== batchKey) {
                        return report;
                    }

                    return { ...report, status: "error" };
                });
            });
        };

        const loadDashboard = async () => {
            try {
                const data = await fetchDashboardManifest(
                    filters,
                    controller.signal,
                );
                if (!mounted || requestIdRef.current !== requestId) {
                    return;
                }

                setDashboard(data);
                setReports(
                    data.reports.map((report) => ({
                        ...report,
                        status: "loading",
                    })),
                );
                setLoading(false);

                const loadBatch = async (batchKey) => {
                    try {
                        const loadedReports = await requestQueue.add(() => {
                            return fetchDashboardBatch(
                                filters,
                                batchKey,
                                controller.signal,
                            );
                        });
                        if (
                            mounted &&
                            requestIdRef.current === requestId
                        ) {
                            replaceBatch(batchKey, loadedReports);
                        }
                    } catch (batchError) {
                        if (
                            mounted &&
                            requestIdRef.current === requestId &&
                            !controller.signal.aborted
                        ) {
                            failBatch(batchKey);
                        }
                    }
                };

                await Promise.all(data.reportBatches.map(loadBatch));
            } catch (manifestError) {
                if (
                    mounted &&
                    requestIdRef.current === requestId &&
                    !controller.signal.aborted
                ) {
                    setError(true);
                    setLoading(false);
                }
            }
        };

        loadDashboard();

        return () => {
            mounted = false;
            requestQueue.cancel();
            controller.abort();
        };
    }, [fetchDashboardManifest, fetchDashboardBatch, filters]);

    const retryReportBatch = async (batchKey) => {
        const requestId = requestIdRef.current;
        setRetrying((current) => ({ ...current, [batchKey]: true }));
        setRetryErrors((current) => ({ ...current, [batchKey]: false }));
        try {
            const requestQueue = requestQueueRef.current;
            const loadedReports = await requestQueue.add(() => {
                return fetchDashboardBatch(
                    filters,
                    batchKey,
                    abortControllerRef.current.signal,
                );
            });
            if (requestIdRef.current !== requestId) {
                return;
            }
            const loadedById = loadedReports.reduce((items, report) => {
                items[report.id] = report;
                return items;
            }, {});
            setReports((current) => {
                return current.map((report) => {
                    const loaded = loadedById[report.id];
                    if (report.batchKey !== batchKey || !loaded) {
                        return report;
                    }

                    return { ...loaded, batchKey, status: "ready" };
                });
            });
        } catch (retryError) {
            if (requestIdRef.current === requestId) {
                setRetryErrors((current) => ({
                    ...current,
                    [batchKey]: true,
                }));
            }
        } finally {
            if (requestIdRef.current === requestId) {
                setRetrying((current) => ({
                    ...current,
                    [batchKey]: false,
                }));
            }
        }
    };

    return (
        <div className={contained ? "container" : ""}>
            <div className='row'>
                <div className='col'>
                    <h2>{title}</h2>
                    {subtitle && <p>{subtitle}</p>}
                </div>
            </div>
            {extraFilters}
            {loading && <CenterLoader />}
            {error && !dashboard && (
                <div className='alert alert-danger'>
                    Failed to load dashboard reports.
                </div>
            )}
            {dashboard && (
                <>
                    <ReportPeriodSelector
                        periods={dashboard.periods}
                        selectedPeriod={dashboard.selectedPeriod}
                        filters={filters}
                        onChange={onFiltersChange}
                    />
                    <ReportGrid
                        reports={reports}
                        onRetry={retryReportBatch}
                        retrying={retrying}
                        retryErrors={retryErrors}
                    />
                </>
            )}
        </div>
    );
}

export default ReportDashboardPage;
